import {
  BinaryBooleanOperator,
  type BooleanOperand,
  type FormkitBoolean,
  type FormkitGraphQLReference,
  type FormkitModifier,
  type FormkitNode,
  type GraphQLVariables,
  ModifierType,
  NodeType,
  ReferenceType,
  UnaryBooleanOperator,
} from "../api";

import { Boolean as Boolean_ } from "./booleans";
import { ContextReference, StateReference } from "./references";

export function ShowModifier(props: {
  condition: FormkitBoolean;
  children: FormkitNode[];
}): FormkitModifier {
  return {
    type: NodeType.Modifier,
    modifier: ModifierType.Show,
    ...props,
  };
}

// If using AsyncReference for an OAuth destination, be sure to add state to the handlers
// i.e. getAuthToken in anaplan/oauth.ts or getEloquaAccessToken in eloqua/oauth.ts
export function AsyncReference(props: {
  children: FormkitNode[];
  handler: FormkitGraphQLReference<GraphQLVariables>;
}): FormkitModifier {
  return {
    type: NodeType.Modifier,
    modifier: ModifierType.AsyncReference,
    ...props,
  };
}

// Reset children components when keys in state change
export function ResetModifier(props: {
  keys: string[];
  children: FormkitNode[];
}): FormkitModifier {
  return {
    type: NodeType.Modifier,
    modifier: ModifierType.Reset,
    ...props,
  };
}

/** When `key` changes in state, switch to that case and reset children */
export function switchOnStateKey(
  key: string,
  cases: {
    // `undefined` targets the unset state (the equality builds a
    // `rightOperand: undefined` condition), used as the catch-all/default case.
    value: string | number | boolean | string[] | number[] | undefined;
    children: FormkitNode[];
  }[],
  referenceType?: ReferenceType.State | ReferenceType.Context,
): FormkitModifier {
  // check cases for duplicate values
  const values = new Set<string | number | boolean | undefined>();
  const expandedCases: {
    value: string | number | boolean | undefined;
    children: FormkitNode[];
  }[] = [];
  for (const { value, children } of cases) {
    if (Array.isArray(value)) {
      for (const valueItem of value) {
        expandedCases.push({ value: valueItem, children });
      }
    } else {
      expandedCases.push({ value, children });
    }
  }
  for (const { value } of expandedCases) {
    if (values.has(value)) {
      throw new Error(`Duplicate cases found with the same value: ${value}`);
    }
    values.add(value);
  }

  return ResetModifier({
    keys: [key],
    children: expandedCases.map((caseItem) => {
      return showIfKeyEqualsValue({
        key,
        ...caseItem,
        referenceType,
      });
    }),
  });
}

export function showIfKeyIsOneOf(props: {
  key: string;
  values: string[] | number[];
  children: FormkitNode[];
}): FormkitNode {
  const { key, values, children } = props;
  const unique = new Set<string | number | boolean>();
  for (const value of values) {
    if (unique.has(value)) {
      throw new Error(`Duplicate cases found with the same value: ${value}`);
    }
    unique.add(value);
  }
  return ResetModifier({
    keys: [key],
    children: values.map((value) => {
      return showIfKeyEqualsValue({
        key,
        value,
        children,
      });
    }),
  });
}

export function showIfKeyIsNotOneOf(props: {
  key: string;
  values: string[] | number[];
  children: FormkitNode[];
}): FormkitNode {
  const { key, values, children } = props;
  return ResetModifier({
    keys: [key],
    children: [
      ShowModifier({
        condition: Boolean_({
          function: "arrayNotIncludes",
          variables: {
            value: StateReference({ key }),
            array: values,
          },
        }),
        children,
      }),
    ],
  });
}

export function splitOnBoolean(
  props: {
    trueBranch: FormkitNode | FormkitNode[];
    falseBranch: FormkitNode | FormkitNode[];
  } & (
    | {
        stateKey: string;
      }
    | {
        contextKey: string;
      }
    | {
        boolCondition: FormkitBoolean;
      }
  ),
) {
  const { trueBranch, falseBranch } = props;
  const trueChildren = Array.isArray(trueBranch) ? trueBranch : [trueBranch];
  const falseChildren = Array.isArray(falseBranch)
    ? falseBranch
    : [falseBranch];

  if ("boolCondition" in props) {
    const { boolCondition } = props;
    return ResetModifier({
      // Keys are empty because we just want a flat layout
      keys: [],
      children: [
        ShowModifier({
          condition: boolCondition,
          children: trueChildren,
        }),
        ShowModifier({
          condition:
            typeof boolCondition === "boolean"
              ? !boolCondition
              : Boolean_({
                  operand: boolCondition,
                  operator: UnaryBooleanOperator.Not,
                }),
          children: falseChildren,
        }),
      ],
    });
  } else {
    const operand =
      "stateKey" in props
        ? StateReference({ key: props.stateKey })
        : ContextReference({ key: props.contextKey });
    const trueCondition = Boolean_({ operand });
    const falseCondition = Boolean_({
      operand: trueCondition,
      operator: UnaryBooleanOperator.Not,
    });
    return ResetModifier({
      keys: "stateKey" in props ? [props.stateKey] : [],
      children: [
        ShowModifier({
          condition: trueCondition,
          children: trueChildren,
        }),
        ShowModifier({
          condition: falseCondition,
          children: falseChildren,
        }),
      ],
    });
  }
}

export function showIfKeyEqualsValue(props: {
  key: string;
  value: string | number | boolean | undefined;
  children: FormkitNode[];
  referenceType?: ReferenceType.State | ReferenceType.Context;
}) {
  const { key, value, children } = props;
  return ResetModifier({
    keys: [key],
    children: [
      ShowModifier({
        condition: Boolean_({
          leftOperand:
            props?.referenceType === ReferenceType.Context
              ? ContextReference({ key })
              : StateReference({ key }),
          operator: BinaryBooleanOperator.Equals,
          // `value` may be `undefined` to target the unset state (e.g. a radio
          // whose "off" option has `value: undefined`). The evaluator handles an
          // undefined rightOperand at runtime, but `BooleanOperand` doesn't model
          // it — and widening that shared type cascades into every operand reader.
          rightOperand: value as BooleanOperand,
        }),
        children: children,
      }),
    ],
  });
}

export function showIfKeyNotEqualsValue(props: {
  key: string;
  value: string | number | boolean;
  children: FormkitNode[];
}) {
  const { key, value, children } = props;
  return ResetModifier({
    keys: [key],
    children: [
      ShowModifier({
        condition: Boolean_({
          leftOperand: StateReference({ key }),
          operator: BinaryBooleanOperator.NotEquals,
          rightOperand: value,
        }),
        children: children,
      }),
    ],
  });
}

type ShowIfProps = {
  key: string;
  children: FormkitNode[];
  /**
   * @default `ReferenceType.State`
   */
  referenceType?: ReferenceType.State | ReferenceType.Context;
};

export function showIfKeyIsTruthy(props: ShowIfProps): FormkitModifier {
  const { key, children, referenceType } = props;
  return ResetModifier({
    keys: [key],
    children: [
      ShowModifier({
        condition: Boolean_({
          operand:
            referenceType === ReferenceType.Context
              ? ContextReference({ key })
              : StateReference({ key }),
        }),
        children,
      }),
    ],
  });
}

export function showIfKeyIsFalsey(props: ShowIfProps): FormkitNode {
  const { key, children, referenceType } = props;
  return ResetModifier({
    keys: [key],
    children: [
      ShowModifier({
        condition: Boolean_({
          operand:
            referenceType === ReferenceType.Context
              ? ContextReference({ key })
              : StateReference({ key }),
          operator: UnaryBooleanOperator.Not,
        }),
        children,
      }),
    ],
  });
}

/**
 * Show children if the value of the key matches the string Regex pattern.
 * The pattern must be a string literal to ensure it does not come from user input.
 */
export function showIfKeyMatchesPattern<T extends string>(props: {
  key: string;
  pattern: T & (string extends T ? never : unknown);
  children: FormkitNode[];
}): FormkitNode {
  const { key, pattern, children } = props;
  return ResetModifier({
    keys: [key],
    children: [
      ShowModifier({
        condition: Boolean_({
          function: "stringPatternMatch",
          variables: {
            value: StateReference({ key }),
            pattern: pattern,
          },
        }),
        children: children,
      }),
    ],
  });
}
